import {
  EMAIL_PLACEHOLDER,
  GENDER_OPTIONS,
  MOBILE_PLACEHOLDER,
} from "@/constants/global.constants";
import { ChevronDownIcon, PencilEdit02Icon } from "@/icons";
import { validateMobile } from "@/utils";
import {
  Button,
  Checkbox,
  DatePicker,
  Flex,
  Form,
  Input,
  Select,
  Switch,
} from "antd";
import dayjs from "dayjs";
import { DB_DATE_FORMAT } from "@/constants/global.constants";
import { useGetAllOfficesQuery } from "../../../../app/features/control-panel/organization/organization.api";
import { useGetAllOvertimePoliciesQuery } from "../../../../app/features/control-panel/policies/policies.api";
import {
  useCreateEmployeeMutation,
  useGetAllDepartmentsQuery,
  useGetAllDesignationsQuery,
  useGetAllEmployeesQuery,
} from "../../../../app/features/employee/emp-dashboard/emp-dashboard.api";
import CustomDrawer from "../../../../components/CustomDrawer";
import ModuleGuard from "../../../../components/Guards/ModuleGuard";
import WithLoader from "../../../../components/WithLoader";
import MODULES from "../../../../config/modules.config";
import useAuth from "../../../../hooks/useAuth";
import useDiscloser from "../../../../hooks/useDiscloser";
import useError from "../../../../hooks/useError";
import { toast } from "../../../../lib/toast";
import { selectArrayOption } from "../../../../utils/global.utils";
import {
  normalizePhoneNumber,
} from "../../../../utils/string.utils";
import ManageDepartment from "./_components/CreateEmployee/ManageDepartment";
import ManageDesignation from "./_components/CreateEmployee/ManageDesignation";
import CustomFieldInputs from "./_components/shared/CustomFieldInputs";
import PayTypeGradeSalaryFields from "./_components/shared/PayTypeGradeSalaryFields";

export default function CreateEmployee({
  open,
  handleClose,
}: {
  data: any;
  type?: string;
  open: boolean;
  handleClose: (value?: boolean) => void;
}) {
  const [form] = Form.useForm();
  const { organizationId, tenantId, profileInfo } = useAuth();

  // ------ Watched values ------
  const departmentId = Form.useWatch("department_id", form);
  const designationId = Form.useWatch("designation_id", form);
  const officeId = Form.useWatch("office_id", form);

  const [config, handleSubOpen, handleSubClose] = useDiscloser<
    "MANAGE_DEPARTMENT" | "MANAGE_DESIGNATION"
  >();

  // const { data: hiredData, isLoading: isHiredLoading } =
  //   useGetAllOfferedApplicantsQuery({
  //     params: {
  //       hiring_pipeline_status: 'hired',
  //       limit: 100,
  //     },
  //   });

  // const handleSelectHired = (id: string) => {
  //   const hired = (hiredData?.data?.data as any[])?.find(
  //     (h) => h.offer_letter_id === id,
  //   );
  //   if (hired) {
  //     form.setFieldsValue({
  //       name: hired.applicant_name,
  //       email: hired.applicant_email,
  //       phone: hired.phone || '+88',
  //       joining_date: hired.joining_date
  //         ? dayjs(hired.joining_date)
  //         : undefined,
  //       employee_type: hired.employee_type,
  //       department_id: hired.department_id,
  //       designation_id: hired.position_id,
  //     });
  //   }
  // };

  const handleSubmit = async (values: any) => {
    try {
      // Transform form data to match API expectations
      const { resign_date, ...rest } = values || {};
      const employeeData = {
        ...rest,
        // Date only: a dayjs object would serialize as UTC and can shift a day.
        ...(resign_date && {
          resign_date: dayjs(resign_date).format(DB_DATE_FORMAT),
        }),
        organization_id: organizationId,
        tenant_id: tenantId,
        phone: normalizePhoneNumber(rest?.phone),
      };

      await createEmployee(employeeData).unwrap();

      toast("success", "Employee created successfully");
      form.resetFields();
      handleClose();
    } catch (error) {
      // Error handled by useError hook
      console.error("Failed to create employee:", error);
    }
  };

  // Get departments and designations for filter dropdowns
  const { data: departmentsData, isLoading: isDepartmentLoading } =
    useGetAllDepartmentsQuery(
      { params: { status: "active" } },
      { skip: !open },
    );
  const { data: designationsData, isLoading: isDesignationLoading } =
    useGetAllDesignationsQuery(
      { params: { status: "active" } },
      { skip: !open },
    );
  const {
    data: employeesData,
    isLoading: employeesLoading,
    error: employeeError,
  } = useGetAllEmployeesQuery({
    params: {
      page: 0,
      limit: 0,
    },
  });
  useError(employeeError, "Failed to get employee");

  // Use API data directly
  const employees = employeesData?.data?.data || [];

  // Create employee mutation
  const [createEmployee, { isLoading: creating, error: createError }] =
    useCreateEmployeeMutation();

  // Get all offices for parent office selection
  const { data: allOfficesData } = useGetAllOfficesQuery(
    { params: { skip: 0, take: 0, status: "ACTIVE" } },
    { skip: !open },
  );

  // Get all overtime policies
  const { data: overtimePoliciesData } = useGetAllOvertimePoliciesQuery(
    { params: { skip: 0, take: 0, office_id: officeId } },
    { skip: !open || !officeId },
  );

  // Error handling
  useError(createError, "Failed to create employee");

  const handleDrawerClose = () => {
    handleClose();
    form.resetFields();
  };
  return (
    <>
      <CustomDrawer
        width={850}
        open={open}
        onClose={handleDrawerClose}
        title={"Add New Employee"}
      >
        <Form
          form={form}
          size="large"
          layout="vertical"
          // requiredMark="optional"
          className="custom-form"
          onFinish={handleSubmit}
        >
          {/* Hired Applicants Select */}
          {/* <div className="mb-6">
            <Form.Item label="Select from Hired Applicants">
              <Select
                showSearch
                allowClear
                placeholder="Select hired applicant to auto-fill form"
                loading={isHiredLoading}
                onChange={handleSelectHired}
                optionFilterProp="label"
                optionLabelProp="label"
                suffixIcon={
                  <ChevronDownIcon size={24} className="text-white" />
                }
              >
                {hiredData?.data?.data?.map((h) => (
                  <Select.Option
                    key={h.offer_letter_id}
                    value={h.offer_letter_id}
                    label={h.applicant_name}
                  >
                    <Flex align="center" gap={12}>
                      <CustomImage
                        width={40}
                        height={40}
                        src={h.photo || IMAGES.avatar}
                        alt={h.applicant_name}
                        placeholder={IMAGES.avatar}
                        className="rounded-full object-cover"
                      />
                      <div className="flex flex-col">
                        <span className="font-semibold">
                          {h.applicant_name}
                        </span>
                        <span className="text-xs text-theme-gray-400">
                          {h.job_title} | {h.department_name}
                        </span>
                      </div>
                    </Flex>
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </div> */}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Full Name */}
            <Form.Item
              label="Full Name"
              name="name"
              rules={[{ required: true, message: "Please enter full name" }]}
            >
              <Input size="large" placeholder="Enter full name" />
            </Form.Item>

            {/* Gender */}
            <Form.Item
              label="Define Gender"
              name="gender"
              rules={[{ required: true, message: "Please select gender" }]}
            >
              <Select
                options={GENDER_OPTIONS}
                size="large"
                placeholder="Select"
              />
            </Form.Item>

            {/* Joining Date */}
            <Form.Item
              label="Joining Date"
              name="joining_date"
              rules={[
                { required: true, message: "Please select date" },
                {
                  validator: (_, value) => {
                    if (!value) return Promise.resolve();
                    const oneYearFromNow = dayjs().add(1, "year");
                    if (dayjs(value).isAfter(oneYearFromNow)) {
                      return Promise.reject(
                        new Error(
                          "The joining date should not be more than one year in the future.",
                        ),
                      );
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <DatePicker size="large" className="w-full" showToday={false} />
            </Form.Item>

            {/* Resignation Date: optional, any employee; feeds payroll pro-rata */}
            <Form.Item
              label="Resignation Date"
              name="resign_date"
              dependencies={["joining_date"]}
              rules={[
                ({ getFieldValue }) => ({
                  validator: (_, value) => {
                    const joined = getFieldValue("joining_date");
                    if (!value || !joined || !dayjs(value).isBefore(joined, "day")) {
                      return Promise.resolve();
                    }
                    return Promise.reject(
                      new Error("Resignation date cannot be before the joining date."),
                    );
                  },
                }),
              ]}
            >
              <DatePicker
                size="large"
                className="w-full"
                placeholder="dd/mm/yyyy"
                showToday={false}
              />
            </Form.Item>

            {/* Employee Type */}
            <Form.Item
              label="Employee Type"
              name="employee_type"
              rules={[{ required: true, message: "Please select type" }]}
            >
              <Select
                size="large"
                className="w-full"
                placeholder="Select Type"
                options={[
                  { value: "full-time", label: "Full Time" },
                  { value: "part-time", label: "Part Time" },
                  { value: "contract", label: "Contract" },
                ]}
              />
            </Form.Item>

            {/* Employee Office */}
            <ModuleGuard moduleId={MODULES.CONTROL_PANEL_ORGANIZATION}>
              <Form.Item
                label="Select Office"
                name="office_id"
                rules={[{ required: true, message: "Please office" }]}
              >
                <Select
                  showSearch
                  allowClear
                  placeholder="Select"
                  suffixIcon={
                    <ChevronDownIcon className="text-white" size={24} />
                  }
                  optionFilterProp="label"
                  options={selectArrayOption(
                    allOfficesData?.data?.data || [],
                    "name",
                    "id",
                  )}
                />
              </Form.Item>
            </ModuleGuard>

            {/* Employee ID */}
            <Form.Item
              label="Employee ID"
              name="employee_id"
              rules={[
                { required: true, message: "Please enter employee ID" },
                {
                  validator: (_, value) => {
                    if (!value) return Promise.resolve(); // empty handled by required

                    const firstChar = value.charAt(0);

                    // Leading space
                    if (/\s/.test(value)) {
                      return Promise.reject(
                        "Employee ID should not contain spaces.",
                      );
                    }

                    // Negative number check (if Employee ID is numeric)
                    if (!isNaN(Number(value)) && Number(value) < 0) {
                      return Promise.reject("Employee ID cannot be negative.");
                    }

                    // Decimal check
                    if (!isNaN(Number(value)) && value.includes(".")) {
                      return Promise.reject(
                        "Employee ID cannot be a decimal number.",
                      );
                    }

                    // Starting special character
                    if (/[^a-zA-Z0-9]/.test(firstChar)) {
                      return Promise.reject(
                        "Employee ID should not start with a special character.",
                      );
                    }

                    // Any space inside
                    if (/\s/.test(value)) {
                      return Promise.reject(
                        "Employee ID leading and trailing spaces should not be allowed.",
                      );
                    }

                    // ✅ Only allow letters, numbers, hyphen (-), underscore (_)
                    const validPattern = /^[a-zA-Z0-9_-]+$/;

                    if (!validPattern.test(value)) {
                      return Promise.reject(
                        "Employee ID accepts letters, numbers, - and _ only.",
                      );
                    }

                    return Promise.resolve();
                  },
                },
              ]}
            >
              <Input size="large" placeholder="Employee ID" />
            </Form.Item>

            {/* Email */}
            <Form.Item
              label="Email"
              name="email"
              rules={[
                { required: true, message: "Please enter email" },
                { type: "email", message: "Please enter a valid email" },
              ]}
            >
              <Input size="large" placeholder={EMAIL_PLACEHOLDER} />
            </Form.Item>

            {/* Mobile Number */}
            <Form.Item
              label="Mobile Number"
              name="phone"
              rules={[
                { required: true, message: "Please enter mobile number" },
                { validator: validateMobile },
              ]}
              initialValue="+88"
            >
              <Input
                size="large"
                placeholder={MOBILE_PLACEHOLDER}
                onChange={(e) => {
                  const value = e.target.value;

                  // Prevent deleting +88
                  if (!value.startsWith("+88")) {
                    form.setFieldsValue({ phone: "+88" });
                  }
                }}
              />
            </Form.Item>

            <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-4 gap-6">
              {/* Geo Fencing */}
              <Form.Item
                label="Geo Fencing"
                name="is_geo_enable"
                layout="horizontal"
                className="border-b border-t !mb-0 !pb-0 border-b-white/40 border-t-white/40 mt-3 horizontal-end"
                valuePropName="checked"
              >
                <Switch className="custom-switch" />
              </Form.Item>

              {/* Flexi Hour */}
              <Form.Item
                label="Flexi Hour"
                name="flexi_hour"
                layout="horizontal"
                className="border-b border-t !mb-0 !pb-0 border-b-white/40 border-t-white/40 mt-3 horizontal-end"
                valuePropName="checked"
              >
                <Switch disabled className="custom-switch" />
              </Form.Item>

              {/* Facial Recognition */}
              <Form.Item
                label="Facial Recognition"
                name="is_face_recognition_enable"
                layout="horizontal"
                className="border-b border-t !mb-0 !pb-0 border-b-white/40 border-t-white/40 mt-3 horizontal-end"
                valuePropName="checked"
              >
                <Switch
                  disabled={
                    !profileInfo?.organization?.meta_hrx
                      ?.is_face_recognition_enable
                  }
                  className="custom-switch"
                />
              </Form.Item>

              {/* Over Time */}
              <ModuleGuard
                moduleId={MODULES.EMPLOYEE_POLICIES}
                fallback={<div />}
              >
                <Form.Item
                  label="Over Time"
                  name="is_overtime_enable"
                  layout="horizontal"
                  className="border-b border-t !mb-0 !pb-0 border-b-white/40 border-t-white/40 mt-3 horizontal-end"
                  valuePropName="checked"
                >
                  <Switch className="custom-switch" />
                </Form.Item>
              </ModuleGuard>
            </div>

            {/* Fence Attendance Permission  */}
            <Form.Item
              label="Fence Attendance Permission"
              name="fence_attendance_permission"
              rules={[{ required: true, message: "Please select permission" }]}
            >
              <Select
                size="large"
                className="w-full"
                placeholder="Select"
                options={[
                  { value: "BOTH", label: "Both" },
                  { value: "CHECK_IN_ONLY", label: "Only Check In" },
                  { value: "CHECK_OUT_ONLY", label: "Only Check Out" },
                ]}
              />
            </Form.Item>

            {/* Overtime Policy - Conditional based on Feature Module Lock */}
            <ModuleGuard
              moduleId={MODULES.EMPLOYEE_POLICIES}
              fallback={<div />}
            >
              <Form.Item
                label="Select Over Time Policy"
                name="overtime_policy_id"
              >
                <Select
                  size="large"
                  className="w-full"
                  placeholder="Select"
                  disabled={!officeId}
                  suffixIcon={
                    <ChevronDownIcon className="text-white" size={24} />
                  }
                  optionFilterProp="label"
                  options={selectArrayOption(
                    overtimePoliciesData?.data?.data || [],
                    "policy_name",
                    "id",
                  )}
                />
              </Form.Item>
            </ModuleGuard>

            <div className="col-span-1 md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Department */}
              <div>
                <div className="flex justify-between items-center mt-2 mb-3">
                  <span className="text-sm text-white">Department *</span>
                  <button
                    type="button"
                    onClick={() => handleSubOpen({ type: "MANAGE_DEPARTMENT" })}
                  >
                    <PencilEdit02Icon
                      size={20}
                      className="text-gray-300 hover:text-white cursor-pointer"
                    />
                  </button>
                </div>
                <Form.Item
                  name="department_id"
                  rules={[
                    { required: true, message: "Please select department" },
                  ]}
                >
                  <WithLoader
                    isLoading={isDepartmentLoading}
                    className="rounded-lg"
                  >
                    <CustomCheckbox
                      className="custom-check"
                      value={departmentId}
                      onChange={(val: any) => {
                        form.setFieldValue("department_id", val);
                      }}
                      options={departmentsData?.data?.data}
                    />
                  </WithLoader>
                </Form.Item>
              </div>

              {/* Designation */}
              <div>
                <div className="flex justify-between items-center mt-2 mb-3">
                  <span className="text-sm text-white">Designation *</span>
                  <button
                    type="button"
                    onClick={() =>
                      handleSubOpen({ type: "MANAGE_DESIGNATION" })
                    }
                  >
                    <PencilEdit02Icon
                      size={20}
                      className="text-gray-300 hover:text-white cursor-pointer"
                    />
                  </button>
                </div>

                <Form.Item
                  name="designation_id"
                  rules={[
                    { required: true, message: "Please select designation" },
                  ]}
                >
                  <WithLoader
                    isLoading={isDesignationLoading}
                    className="rounded-lg"
                  >
                    <CustomCheckbox
                      className="custom-check"
                      value={designationId}
                      onChange={(val: any) => {
                        form.setFieldValue("designation_id", val);
                      }}
                      options={designationsData?.data?.data}
                    />
                  </WithLoader>
                </Form.Item>
              </div>
            </div>

            {/* Assigned To */}
            <Form.Item
              label="Assigned To"
              name="manager_id"
              rules={[{ required: true, message: "Please select manager" }]}
            >
              <Select
                size="large"
                className="w-full"
                placeholder="Select Manager"
                allowClear
                showSearch
                optionFilterProp="label"
                loading={employeesLoading}
                options={employees?.map((emp) => ({
                  label: emp?.name,
                  value: emp?.user_id,
                }))}
              />
            </Form.Item>

            <PayTypeGradeSalaryFields />
          </div>

          {/* The company's own extra fields. Renders nothing when it has none. */}
          <CustomFieldInputs />

          <Flex align="center" justify="end" gap={16} className="mt-6">
            <Button
              size="middle"
              htmlType="button"
              className="min-w-[120px] h-10 !border-teal-500 !text-teal-500 !bg-transparent !font-semibold"
              onClick={handleDrawerClose}
            >
              Cancel
            </Button>
            <Button
              size="middle"
              htmlType="submit"
              type="primary"
              color="primary"
              loading={creating}
              className="min-w-[120px] h-10 !bg-teal-500 !text-neutral-700 !border-none !font-semibold"
            >
              Confirm
            </Button>
          </Flex>
        </Form>
      </CustomDrawer>

      {/* Manage Department Modal */}
      {config.open && config.type === "MANAGE_DEPARTMENT" && (
        <ManageDepartment handleClose={handleSubClose} open={config.open} />
      )}

      {/* Manage Department Modal */}
      <ManageDesignation
        handleClose={handleSubClose}
        open={config.open && config.type === "MANAGE_DESIGNATION"}
      />
    </>
  );
}

function CustomCheckbox({
  value,
  onChange,
  options,
  className,
}: {
  value: string | null;
  onChange: (val: string | null) => void;
  options: any;
  className?: string;
}) {
  const handleSelect = (val: string) => {
    onChange(value === val ? null : val);
  };

  return (
    <div
      className={`space-y-2 ${className || ""} max-h-[150px] overflow-y-auto`}
    >
      {options?.map((option: any) => {
        return (
          <label
            key={option?.id}
            className="flex items-center gap-2 cursor-pointer select-none"
          >
            <Checkbox
              value={option?.id}
              checked={value === option?.id}
              onChange={() => handleSelect(option?.id)}
            />
            {option?.name}
          </label>
        );
      })}
    </div>
  );
}
